import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Menu,
  Car,
  Bell,
  LayoutGrid,
  Calendar,
  Clipboard,
  Building2,
  Search,
  X,
  DollarSign,
  MessageSquare,
  XCircle,
  ChevronDown,
  Shield,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { NavLogo } from "./navbar/NavLogo";
import { DesktopMenu } from "./navbar/DesktopMenu";
import { MobileMenu } from "./navbar/MobileMenu";
import { GetTheAppNavButton } from "./navbar/GetTheAppNavButton";
import { TopAnnouncementBar } from "./navbar/TopAnnouncementBar";
import { useNotificationBadge } from "@/hooks/use-notification-badge";
import { cn } from "@/lib/utils";
import { cleanupRadixScrollLock } from "@/lib/utils/radix-scroll-lock";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySelector } from "@/components/currency/CurrencySelector";
import { useTranslation } from "react-i18next";
import { AdminNotificationDropdown } from "./admin/AdminNotificationDropdown";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ADMIN_NAV_ITEMS,
  readAdminScopeFromUser,
  scopeIsAllowed,
} from "@/lib/admin/adminScope";
import { adminNavIcon } from "@/lib/admin/adminNavIcons";

const SCROLL_THRESHOLD_PX = 12;

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const authLoading = auth?.isLoading ?? false;
  const signOut = auth?.signOut ?? (async () => {});
  const getUserRole = auth?.getUserRole ?? (async () => null);
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState<string | null>(null);
  const { unreadCount } = useNotificationBadge();

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setUserRole(null);
      return;
    }

    void (async () => {
      try {
        const role = await getUserRole();
        if (!cancelled) {
          setUserRole(role);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du rôle:", error);
        if (!cancelled) {
          setUserRole(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, getUserRole]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignOut = useCallback(async () => {
    setIsOpen(false);
    cleanupRadixScrollLock();
    try {
      await signOut();
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
    navigate("/", { replace: true });
  }, [signOut, navigate]);

  const handleBecomeOwner = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Rediriger directement vers la page d'information, peu importe le statut de l'utilisateur
      navigate("/agences");
    },
    [navigate]
  );

  const getInitials = useCallback(
    (
      user: { user_metadata?: { first_name?: string; last_name?: string }; email?: string } | null
    ) => {
      const firstName = user?.user_metadata?.first_name || "";
      const lastName = user?.user_metadata?.last_name || "";

      // Gérer les cas où firstName ou lastName est vide/null
      const firstInitial = firstName && firstName.length > 0 ? firstName[0] : "";
      const lastInitial = lastName && lastName.length > 0 ? lastName[0] : "";

      // Si on a au moins une initiale, les utiliser, sinon utiliser l'email
      if (firstInitial || lastInitial) {
        return (firstInitial + lastInitial).toUpperCase();
      }

      // Fallback sur l'email si pas de noms
      const email = user?.email || "";
      if (email && email.length > 0) {
        return email[0].toUpperCase();
      }

      return "U"; // Dernier fallback
    },
    []
  );

  const isOwner = userRole === "owner";
  const isRenter = userRole === "renter";
  const isAdmin = userRole === "admin";
  const adminScope = readAdminScopeFromUser(user);
  const canUseImpersonationViews = adminScope === "super";
  const visibleAdminNav = ADMIN_NAV_ITEMS.filter((item) => scopeIsAllowed(adminScope, item.scopes));

  const isSearchPage = location.pathname === "/search";

  /** Pages with light background (e.g. /search): use dark nav styles so CTAs stay visible */
  const useLightNavStyles = isScrolled || isSearchPage;

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const isExactActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Fragment>
      {/* Wrapper fixe : header Navbar */}
      <div className="fixed top-0 inset-x-0 z-[60] flex w-full flex-col pt-[env(safe-area-inset-top,0px)]">
        <TopAnnouncementBar
          isAuthenticated={Boolean(user)}
          userRole={userRole}
          authLoading={authLoading}
        />
        {/* Header principal - Solid when logged in, transparent/blur on scroll when logged out */}
        <header
          className={cn(
            "sticky top-0 z-50 w-full border-b transition-[background-color,box-shadow,border-color,backdrop-filter] duration-300 ease-out",
            user
              ? "bg-scandi-surface border-border shadow-soft"
              : isScrolled || isSearchPage
                ? "border-border/80 bg-white/95 shadow-sm backdrop-blur-md dark:bg-background/95 dark:border-border dark:shadow-none"
                : "border-transparent bg-transparent supports-[backdrop-filter]:bg-white/[0.04] supports-[backdrop-filter]:backdrop-blur-[6px]"
          )}
        >
          <div
            data-navbar-row
            className={cn(
              "w-full flex items-center justify-between gap-2 sm:gap-3 transition-all duration-300 relative",
              user ? "min-h-16 sm:min-h-20 md:min-h-20 py-1" : "min-h-12 sm:min-h-14 md:min-h-14 py-0.5",
              isRtl && !user && "md:min-h-[3.75rem]"
            )}
          >
            <div
              className={cn(
                "ps-3 sm:ps-5 lg:ps-8 relative z-10 shrink-0",
                !user && "flex items-center min-h-[2.25rem] sm:min-h-[2.5rem]"
              )}
            >
              <NavLogo />
            </div>

            {/* Logged-in: Center = Role-based navigation links */}
            {user && userRole && (
              <nav className="hidden md:flex items-center gap-1.5 flex-1 justify-center mx-4 lg:mx-8 relative z-10 min-w-0 overflow-x-auto">
                {isOwner && (
                  <>
                    <Link
                      to="/dashboard/owner"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isExactActive("/dashboard/owner")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <LayoutGrid className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isExactActive("/dashboard/owner")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.dashboard')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/owner/vehicles"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/owner/vehicles")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <Car className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/owner/vehicles")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.myVehicles')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/owner/fleet"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/owner/fleet")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <Clipboard className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/owner/fleet")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.fleetAvailability')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/owner/bookings"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/owner/bookings")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <Calendar className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/owner/bookings")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.bookings')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/owner/revenue"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/owner/revenue")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <DollarSign className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/owner/revenue")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.revenue')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/messages"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/messages")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <MessageSquare className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/messages")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.messages')}</span>
                      </span>
                    </Link>
                  </>
                )}
                {isRenter && (
                  <>
                    <Link
                      to="/search"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/search")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <Search className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/search")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.search')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/renter/bookings"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/renter/bookings")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <Calendar className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/renter/bookings")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.myBookings')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/renter"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isExactActive("/dashboard/renter")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <LayoutGrid className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isExactActive("/dashboard/renter")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.dashboard')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/messages"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/messages")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <MessageSquare className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/messages")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.messages')}</span>
                      </span>
                    </Link>
                    <Link
                      to="/dashboard/renter/cancellations"
                      className={cn(
                        "group relative px-5 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        isActive("/dashboard/renter/cancellations")
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-3 relative z-10">
                        <XCircle className={cn(
                          "w-4 h-4 transition-all duration-300",
                          isActive("/dashboard/renter/cancellations")
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t('nav.myCancellations')}</span>
                      </span>
                    </Link>
                  </>
                )}
                {isAdmin && visibleAdminNav.map((item) => {
                  const Icon = adminNavIcon(item.path);
                  const active = item.path === "/admin" ? location.pathname === "/admin" : isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "group relative whitespace-nowrap px-3 py-2 text-sm font-semibold rounded-2xl transition-all duration-300 no-underline hover:no-underline",
                        "hover:scale-105 active:scale-95 hover:shadow-md",
                        active
                          ? "text-green-night bg-emerald-soft shadow-md border border-green-night/10"
                          : "text-text-secondary hover:text-green-night hover:bg-emerald-soft/60 hover:border hover:border-emerald-subtle/50"
                      )}
                    >
                      <span className="flex items-center gap-2 relative z-10">
                        <Icon className={cn(
                          "w-4 h-4 transition-all duration-300",
                          active
                            ? "scale-110 text-green-night"
                            : "group-hover:scale-110 group-hover:text-green-night"
                        )} />
                        <span className="font-medium">{t(item.labelKey)}</span>
                      </span>
                    </Link>
                  );
                })}
              </nav>
            )}

            {/* Desktop/tablet menu (md+) */}
            <div
              className={cn(
                "hidden md:flex items-center flex-wrap justify-end gap-1.5 sm:gap-2 shrink-0 min-w-0",
                "ps-2 pe-3 sm:pe-5 lg:pe-8 relative z-10",
                isRtl && "gap-2"
              )}
            >
              <LanguageSwitcher
                useLightSurface={Boolean(user) || useLightNavStyles}
                className={cn(isRtl && "shrink-0")}
              />
              <CurrencySelector className="shrink-0" />

              {/* Admin dropdown - visible when admin or on admin path */}
              {user && (isAdmin || location.pathname.startsWith("/admin")) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "gap-1.5 rounded-xl font-medium text-sm hover:bg-emerald-soft/60 hover:text-green-night transition-all duration-200",
                        location.pathname.startsWith("/admin")
                          ? "bg-emerald-soft text-green-night border border-green-night/10"
                          : "text-text-secondary"
                      )}
                      aria-label={t('nav.adminMenuAria')}
                    >
                      <Shield className="h-4 w-4 shrink-0" />
                      <span>Admin</span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-[70vh] w-64 overflow-y-auto rounded-xl border-border shadow-lg">
                    {visibleAdminNav.map((item) => {
                      const Icon = adminNavIcon(item.path);
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link to={item.path} className="flex items-center gap-2 cursor-pointer">
                            <Icon className="h-4 w-4" />
                            {t(item.labelKey)}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                    {canUseImpersonationViews && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="flex items-center gap-2 cursor-pointer"
                          onSelect={() => {
                            cleanupRadixScrollLock();
                            navigate("/dashboard/owner");
                          }}
                        >
                          <Eye className="h-4 w-4" />
                          {t("nav.viewAsHost")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="flex items-center gap-2 cursor-pointer"
                          onSelect={() => {
                            cleanupRadixScrollLock();
                            navigate("/dashboard/renter");
                          }}
                        >
                          <Eye className="h-4 w-4" />
                          {t("nav.viewAsRenter")}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Lien "Pour les agences" - blanc sur hero, noir au scroll */}
              {!user && (
                <>
                  <div
                    onClick={handleBecomeOwner}
                    role="presentation"
                    className={cn(
                      "flex cursor-pointer touch-manipulation items-center gap-1.5 sm:gap-2 rounded-full border px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold transition-all duration-300 ease-out active:scale-[0.99] shrink-0",
                      useLightNavStyles
                        ? "border-border bg-white text-neutral-900 shadow-sm hover:border-green-night/30 hover:bg-scandi-bg"
                        : "border-white/50 bg-white/15 text-white shadow-[0_1px_0_rgba(255,255,255,0.12)_inset] backdrop-blur-md hover:border-white/70 hover:bg-white/25"
                    )}
                  >
                    <Link
                      to="/agences"
                      className={cn(
                        "flex min-w-0 items-center gap-1.5 no-underline hover:no-underline sm:gap-2",
                        useLightNavStyles
                          ? "!text-neutral-900 hover:!text-neutral-900"
                          : "!text-white hover:!text-white"
                      )}
                    >
                      <Building2 className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="hidden whitespace-nowrap xl:inline">
                        {t("nav.forAgencies")}
                      </span>
                      <span className="whitespace-nowrap xl:hidden">
                        {t("nav.forAgenciesShort", { defaultValue: t("nav.forAgencies") })}
                      </span>
                    </Link>
                  </div>
                  <span
                    className={cn(
                      "text-sm mx-0.5 sm:mx-1.5 select-none shrink-0 hidden lg:inline",
                      useLightNavStyles ? "text-muted-foreground" : "text-white/80"
                    )}
                    aria-hidden="true"
                  >
                    |
                  </span>
                </>
              )}

              {/* Notifications bell for non-admin logged-in users; admin uses AdminNotificationDropdown */}
              {user && (
                isAdmin ? (
                  <ErrorBoundary>
                    <AdminNotificationDropdown />
                  </ErrorBoundary>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative h-11 w-11 rounded-2xl hover:bg-emerald-soft/60 transition-all duration-300 group hover:scale-110 active:scale-95 hover:shadow-md border border-transparent hover:border-emerald-subtle/50"
                    onClick={() => navigate("/notifications")}
                    aria-label={unreadCount > 0 ? t('nav.notificationsAria', { count: unreadCount }) : t('nav.notifications')}
                  >
                    <Bell
                      className={cn(
                        "w-5 h-5 transition-all duration-300",
                        unreadCount > 0
                          ? "text-green-night group-hover:text-green-light group-hover:animate-pulse"
                          : "text-text-secondary group-hover:text-green-night"
                      )}
                    />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-green-night text-white text-[10px] font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5 shadow-lg border-2 border-white ring-2 ring-green-night/20 animate-pulse">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Button>
                )
              )}

              <DesktopMenu
                user={user}
                onSignOut={handleSignOut}
                getInitials={getInitials}
                unreadCount={unreadCount}
                isScrolled={useLightNavStyles}
                compactLabels={isRtl}
              />
            </div>

            {/* Mobile: show currency selector + store CTA + menu (hidden md+) */}
            <div className="flex items-center gap-1.5 md:hidden shrink-0 ms-auto pe-2 sm:pe-4">
              <CurrencySelector className="shrink-0 max-w-[56vw] overflow-x-auto" />
              <GetTheAppNavButton />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                  "relative z-10 h-11 w-11 rounded-2xl border border-transparent pr-2 transition-all duration-300 ease-out hover:scale-[1.02] hover:border-emerald-subtle/40 hover:bg-emerald-soft/55 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night focus-visible:ring-offset-2 active:scale-[0.98] sm:h-12 sm:w-12 sm:pr-4",
                  useLightNavStyles
                    ? "text-foreground hover:text-foreground"
                    : "text-white hover:bg-white/10 hover:text-white"
                )}
                aria-label={t('nav.toggleMenu')}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <X className="h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300 rotate-90" />
                ) : (
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300" />
                )}
                <span className="sr-only">Menu</span>
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          <MobileMenu
            user={user}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onSignOut={handleSignOut}
            getInitials={getInitials}
            unreadCount={unreadCount}
            onBecomeOwner={handleBecomeOwner}
          />

        </header>
      </div>
    </Fragment>
  );
};

export default Navbar;
